import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import supabase from '../supabase/supabase';
import { generateId } from '../utils/helpers';

const StoreContext = createContext();

const TABLE_MAP = {
  users: 'users_2024',
  categories: 'categories_2024',
  businessUnits: 'business_units_2024',
  taskTemplates: 'task_templates_2024',
  workflowTemplates: 'workflow_templates_2024',
  projects: 'projects_2024',
  workspaces: 'workspaces_2024',
  workspaceMembers: 'workspace_members_2024',
  quickLists: 'quick_lists_2024'
};

const ROLE_LIMITS = {
  master_admin: { bus: 9999, projectsPerBu: 9999, members: 9999 },
  admin_enterprise: { bus: 9999, projectsPerBu: 9999, members: 9999 },
  admin_gold: { bus: 5, projectsPerBu: 20, members: 5 },
  admin_bronze: { bus: 1, projectsPerBu: 5, members: 0 },
  member: { bus: 0, projectsPerBu: 0, members: 0 }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_STATE':
      return { ...state, ...action.payload, loading: false };
    case 'SET_USER':
      return { ...state, currentUser: action.payload };
    case 'SET_WORKSPACE':
      return { ...state, currentWorkspace: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_RECOVERY_MODE':
      return { ...state, isRecoveryMode: action.payload };
    default:
      return state;
  }
};

// Extracts a human-readable message from any shape of error Supabase
// (or the browser) might throw, instead of showing a blank/generic string.
const describeError = (error) => {
  if (!error) return 'Unknown error.';
  if (typeof error === 'string') return error;

  const parts = [error.message, error.details, error.hint, error.code]
    .filter(Boolean);

  if (parts.length > 0) return parts.join(' — ');

  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error.';
  }
};

export const StoreProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    users: {},
    categories: {},
    businessUnits: {},
    taskTemplates: {},
    workflowTemplates: {},
    projects: {},
    quickLists: {},
    workspaces: {},
    workspaceMembers: {},
    currentUser: null,
    currentWorkspace: null,
    loading: true,
    isRecoveryMode: false
  });

  const isInitialized = useRef(false);

  const mapFromDb = (item) => {
    if (!item) return item;

    const mapped = { ...item };

    if (item.logo_url) mapped.logoUrl = item.logo_url;
    if (item.app_url) mapped.appUrl = item.app_url;
    if (item.duration_minutes !== undefined) {
      mapped.durationMinutes = item.duration_minutes;
    }
    if (item.business_unit_id) mapped.businessUnitId = item.business_unit_id;
    if (item.category_id) mapped.categoryId = item.category_id;
    if (item.workspace_id) mapped.workspaceId = item.workspace_id;
    if (item.start_date) mapped.startDate = item.start_date;
    if (item.is_app_task !== undefined) mapped.isAppTask = item.is_app_task;
    if (item.parent_id) mapped.parentId = item.parent_id;

    return mapped;
  };

  const mapToDb = (item) => {
    if (!item) return item;

    const dbItem = { ...item };

    const uiFields = [
      'totalHours',
      'totalMinutes',
      'calculatedEndDate',
      'scheduledWorkflows',
      'isFullyComplete',
      'hours',
      'minutes',
      'calculatedStatus',
      '_wfIndex',
      '_taskIndex'
    ];

    uiFields.forEach((field) => delete dbItem[field]);

    if ('logoUrl' in dbItem) {
      dbItem.logo_url = dbItem.logoUrl;
      delete dbItem.logoUrl;
    }

    if ('appUrl' in dbItem) {
      dbItem.app_url = dbItem.appUrl;
      delete dbItem.appUrl;
    }

    if ('durationMinutes' in dbItem) {
      dbItem.duration_minutes = dbItem.durationMinutes;
      delete dbItem.durationMinutes;
    }

    if ('businessUnitId' in dbItem) {
      dbItem.business_unit_id = dbItem.businessUnitId;
      delete dbItem.businessUnitId;
    }

    if ('categoryId' in dbItem) {
      dbItem.category_id = dbItem.categoryId;
      delete dbItem.categoryId;
    }

    if ('workspaceId' in dbItem) {
      dbItem.workspace_id = dbItem.workspaceId;
      delete dbItem.workspaceId;
    }

    if ('startDate' in dbItem) {
      dbItem.start_date = dbItem.startDate;
      delete dbItem.startDate;
    }

    if ('isAppTask' in dbItem) {
      dbItem.is_app_task = dbItem.isAppTask;
      delete dbItem.isAppTask;
    }

    if ('parentId' in item) {
      dbItem.parent_id = item.parentId;
    }

    return dbItem;
  };

  const handleAuthSession = useCallback(async (session) => {
    if (!session?.user) {
      isInitialized.current = false;
      dispatch({ type: 'SET_USER', payload: null });
      dispatch({ type: 'SET_WORKSPACE', payload: null });
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    try {
      const email = session.user.email.toLowerCase();
      const userId = session.user.id;
      const metadata = session.user.user_metadata || {};

      let targetRole = metadata.role || 'member';

      if (email === 'accounts@questican.com') {
        targetRole = 'master_admin';
      }

      const { data: existingProfile } = await supabase
        .from('users_2024')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      const profileData = {
        id: userId,
        email,
        name: existingProfile?.name || metadata.name || email.split('@')[0],
        role: existingProfile?.role || targetRole,
        parent_id: existingProfile?.parent_id || metadata.parent_id || null,
        permissions: existingProfile?.permissions ||
          metadata.permissions || {
            canCreateProjects: targetRole !== 'member'
          }
      };

      const { data: profile } = await supabase
        .from('users_2024')
        .upsert(profileData)
        .select()
        .single();

      const { data: memberships } = await supabase
        .from('workspace_members_2024')
        .select('workspace_id')
        .eq('user_id', userId);

      let activeWorkspace = null;

      if (!memberships || memberships.length === 0) {
        const workspaceId = generateId('ws');
        const limits = ROLE_LIMITS[profile.role] || ROLE_LIMITS.admin_bronze;

        await supabase.from('workspaces_2024').insert({
          id: workspaceId,
          name: `${profile.name}'s Workspace`,
          owner_id: userId,
          max_bus: limits.bus,
          max_projects_per_bu: limits.projectsPerBu,
          max_members: limits.members
        });

        await supabase.from('workspace_members_2024').insert({
          id: generateId('wm'),
          workspace_id: workspaceId,
          user_id: userId,
          role: profile.role.startsWith('admin') ||
            profile.role === 'master_admin'
            ? 'admin'
            : 'user'
        });

        const { data: newWorkspace } = await supabase
          .from('workspaces_2024')
          .select('*')
          .eq('id', workspaceId)
          .single();

        activeWorkspace = newWorkspace;
      } else {
        const { data: workspace } = await supabase
          .from('workspaces_2024')
          .select('*')
          .eq('id', memberships[0].workspace_id)
          .single();

        activeWorkspace = workspace;
      }

      dispatch({
        type: 'SET_USER',
        payload: mapFromDb(profile)
      });

      dispatch({
        type: 'SET_WORKSPACE',
        payload: mapFromDb(activeWorkspace)
      });
    } catch (error) {
      console.error('Auth sync failed:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const fetchData = useCallback(async (isBackground = false) => {
    if (!state.currentWorkspace || !state.currentUser) return;

    if (!isBackground) {
      dispatch({ type: 'SET_LOADING', payload: true });
    }

    try {
      const workspaceId = state.currentWorkspace.id;
      const payload = {};
      const tables = [
        'categories',
        'businessUnits',
        'taskTemplates',
        'workflowTemplates',
        'projects',
        'quickLists'
      ];

      for (const key of tables) {
        const { data, error } = await supabase
          .from(TABLE_MAP[key])
          .select('*')
          .eq('workspace_id', workspaceId);

        if (error) throw error;

        payload[key] = (data || []).reduce(
          (accumulator, item) => ({
            ...accumulator,
            [item.id]: mapFromDb(item)
          }),
          {}
        );
      }

      let userQuery = supabase.from('users_2024').select('*');

      if (state.currentUser.role !== 'master_admin') {
        userQuery = userQuery.eq('parent_id', state.currentUser.id);
      }

      const { data: userProfiles, error: usersError } = await userQuery;

      if (usersError) throw usersError;

      payload.users = (userProfiles || []).reduce(
        (accumulator, user) => ({
          ...accumulator,
          [user.id]: mapFromDb(user)
        }),
        {}
      );

      dispatch({ type: 'SET_STATE', payload });
      isInitialized.current = true;
    } catch (error) {
      console.error('Fetch failed:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.currentWorkspace, state.currentUser]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        handleAuthSession(session);
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        ['SIGNED_IN', 'TOKEN_REFRESHED', 'PASSWORD_RECOVERY'].includes(event)
      ) {
        if (session) handleAuthSession(session);
      } else if (event === 'SIGNED_OUT') {
        handleAuthSession(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [handleAuthSession]);

  useEffect(() => {
    if (state.currentWorkspace && state.currentUser) {
      fetchData(isInitialized.current);
    }
  }, [state.currentWorkspace, state.currentUser, fetchData]);

  // Archiving is a narrow, high-value operation — keep it isolated from the
  // generic update path and make failures fully diagnosable.
  const archiveProject = async (item) => {
    if (!item?.id) {
      throw new Error('Cannot archive a project without an ID.');
    }

    const workspaceId = state.currentWorkspace?.id;

    if (!workspaceId) {
      throw new Error('No active workspace found. Please refresh and try again.');
    }

    // Primary attempt: scoped to the current workspace.
    let { data, error } = await supabase
      .from('projects_2024')
      .update({ status: 'archived' })
      .eq('id', item.id)
      .eq('workspace_id', workspaceId)
      .select();

    if (error) {
      console.error('Archive update error:', error);
      throw new Error(`Supabase rejected the archive request: ${describeError(error)}`);
    }

    // Defensive fallback: if nothing matched with the workspace filter
    // (e.g. a stale/legacy row with a different workspace_id casing),
    // retry once without that filter, scoped only by ID.
    if (!data || data.length === 0) {
      const retry = await supabase
        .from('projects_2024')
        .update({ status: 'archived' })
        .eq('id', item.id)
        .select();

      if (retry.error) {
        console.error('Archive retry error:', retry.error);
        throw new Error(`Supabase rejected the archive request: ${describeError(retry.error)}`);
      }

      data = retry.data;
    }

    if (!data || data.length === 0) {
      throw new Error(
        `No project with ID "${item.id}" could be found to archive. It may have already been removed.`
      );
    }

    await fetchData(true);
    return data[0];
  };

  const updateEntity = async (type, item) => {
    const tableName = TABLE_MAP[type];

    if (!tableName) {
      throw new Error(`Unknown entity type: ${type}`);
    }

    if (!item?.id) {
      throw new Error('Cannot update an entity without an ID.');
    }

    if (type === 'projects' && item.status === 'archived') {
      return archiveProject(item);
    }

    const dbItem = mapToDb(item);
    delete dbItem.id;

    const { error } = await supabase
      .from(tableName)
      .update(dbItem)
      .eq('id', item.id)
      .eq('workspace_id', state.currentWorkspace.id);

    if (error) {
      console.error(`Update error on ${tableName}:`, error);
      throw new Error(describeError(error));
    }

    await fetchData(true);
  };

  const addEntity = async (type, item) => {
    const tableName = TABLE_MAP[type];

    if (!tableName) {
      throw new Error(`Unknown entity type: ${type}`);
    }

    const dbItem = mapToDb({
      ...item,
      workspace_id: state.currentWorkspace.id
    });

    const { data, error } = await supabase
      .from(tableName)
      .insert(dbItem)
      .select();

    if (error) {
      console.error(`Insert error on ${tableName}:`, error);
      throw new Error(describeError(error));
    }

    await fetchData(true);
    return data;
  };

  const deleteEntity = async (type, id) => {
    const tableName = TABLE_MAP[type];

    if (!tableName) {
      throw new Error(`Unknown entity type: ${type}`);
    }

    const { error } = await supabase
      .from(tableName)
      .delete()
      .eq('id', id)
      .eq('workspace_id', state.currentWorkspace.id);

    if (error) {
      console.error(`Delete error on ${tableName}:`, error);
      throw new Error(describeError(error));
    }

    await fetchData(true);
  };

  return (
    <StoreContext.Provider
      value={{
        state,
        login: (email, password) =>
          supabase.auth.signInWithPassword({
            email: email.trim().toLowerCase(),
            password
          }),
        signup: (user) =>
          supabase.auth.signUp({
            email: user.email.trim().toLowerCase(),
            password: user.password,
            options: {
              data: { name: user.name }
            }
          }),
        logout: () => {
          isInitialized.current = false;
          supabase.auth.signOut();
        },
        refresh: () => fetchData(true),
        addEntity,
        updateEntity,
        deleteEntity
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);